/// <reference types="vite/client" />
import { createClient } from "@supabase/supabase-js"
import { isMockMode, mockSupabaseClient } from "@/lib/mock/mock-supabase"

/**
 * IMPORTANTE: Nunca acessar o arquivo .env diretamente.
 * As variáveis VITE_SUPABASE_URL e VITE_SUPABASE_ANON_KEY devem estar
 * definidas no arquivo .env na raiz do projeto.
 *
 * O banco Supabase é compartilhado entre projetos da Invenia.
 * TODAS as tabelas deste projeto usam o prefixo: credit_v2_
 *
 * Quando VITE_SUPABASE_URL estiver vazio, o cliente mock é usado
 * automaticamente para modo demo/preview.
 */

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY as string

if (!isMockMode && (!supabaseUrl || !supabaseAnonKey)) {
  throw new Error(
    "Variáveis de ambiente VITE_SUPABASE_URL e VITE_SUPABASE_ANON_KEY são obrigatórias. " +
      "Configure o arquivo .env na raiz do projeto.",
  )
}

/**
 * Cliente Supabase para uso no frontend.
 * Em modo mock (sem VITE_SUPABASE_URL), retorna um cliente mock.
 * Usa a chave ANON — segurança garantida pelo RLS nas tabelas credit_v2_*.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const supabase: any = isMockMode
  ? mockSupabaseClient
  : createClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        persistSession: true,
        detectSessionInUrl: true,
        autoRefreshToken: true,
      },
      realtime: {
        params: {
          eventsPerSecond: 10,
        },
      },
      global: {
        headers: {},
      },
    })

/**
 * Retorna a URL pública de um arquivo no Storage do Supabase.
 */
export function getPublicUrl(bucket: string, path: string): string {
  if (isMockMode) return ""
  const { data } = supabase.storage.from(bucket).getPublicUrl(path)
  return data.publicUrl
}

/**
 * Cria uma Signed URL temporária para acesso privado a documentos de leads.
 * @param expiresInSeconds - Validade da URL em segundos (padrão: 1 hora)
 */
export async function createSignedUrl(
  bucket: string,
  path: string,
  expiresInSeconds = 3600,
): Promise<string | null> {
  if (isMockMode) return null

  const { data, error } = await supabase.storage
    .from(bucket)
    .createSignedUrl(path, expiresInSeconds)

  if (error) {
    console.error("Erro ao criar Signed URL:", error)
    return null
  }

  return data.signedUrl
}

export default supabase
